import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import FlatButton from '../../components/buttons/flat_button'
import BaseCard from '../../components/cards/base_card'
import { EvaluationStarIcon } from '../../components/common/star_icons'
import { GivedPointIcon } from '../../images/gived_point_icon'
import { HoldPointIcon } from '../../images/hold_point_icon'
import { IinePointIcon } from '../../images/iine_point_icon'
import RankItem from './components/rank_item'
import { useGuideStatus } from './hooks/use_guide_status'
import LoadingSpinner from '../../components/loading/loading_spinner'
import { useOwnDetail } from '../../hooks/kuchikomi/use_own_detail'
import { SDGsRankItem } from './components/sdgs_rank_item'
import { useOwnKuchikomis } from '../../hooks/kuchikomi/use_own_kuchikomis'
import { useOwnRecommendation } from '../../hooks/use_own_recommendation'
import { useOwnRecruitment } from '../../hooks/use_own_recruitment'
import { useOwnFavorite } from '../../hooks/use_own_favorite'
import { useOwnAsiato } from '../../hooks/use_own_asiato'
import EnterpriseCard from '../../components/cards/enterprise_card'
import BusinessIdeaCard from '../../components/cards/business_idea_card'
import EnterpriseStatCard from '../../components/cards/enterprise_stat_card'
import StatCard from '../../components/cards/stat_card'
import PartnerRecruitmentCard from '../../components/cards/partner_recruitment_card'
import { PeriodType, getPeriodDays } from '../../enums/PeriodType'
import { useAuth } from '../../hooks/auth/use_auth'
import dayjs from 'dayjs'
import { getPublicPath } from '../../utils/view_util'
import { useBusinessDelete } from '../business_detail/hooks/use_business_delete'
import DeleteConfirmModal from '../../components/modals/delete_confirm_modal'

const TopPage: React.FC = () => {
  // ナビゲーション
  const navigate = useNavigate()
  // 認証
  const { user } = useAuth()
  // 削除機能
  const { isDeleting, deleteBusinessIdea } = useBusinessDelete()
  // 削除確認モーダル
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  // スタートアップガイド
  const { initialized: initializedGuideStatus, completedActions } =
    useGuideStatus()
  // 企業詳細・口コミ情報
  const { initialized: initializedEnterprise, enterprise } = useOwnDetail()
  const { initialized, kuchikomis } = useOwnKuchikomis()
  // 事業アイディア
  const { isLoading, recommendations, load } = useOwnRecommendation(3)

  // 共創パートナー募集
  const { isLoading: loadingRecruitment, recruitments } = useOwnRecruitment(4)
  // お気に入り企業
  const { isLoading: loadingFavorite, enterprises } = useOwnFavorite(3)

  const { start, end } = useMemo(() => {
    const now = dayjs()
    const days = getPeriodDays(PeriodType.SEVEN_DAYS)
    return {
      start: now.subtract(days, 'day').format('YYYY-MM-DD'),
      end: now.format('YYYY-MM-DD'),
    }
  }, [])

  // 事業アイデア削除ハンドラー
  const handleDelete = (businessIdeaId: string, projectName: string) => {
    setDeleteTarget({ id: businessIdeaId, name: projectName })
    setIsDeleteModalOpen(true)
  }

  // 削除確認処理
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return

    const success = await deleteBusinessIdea(deleteTarget.id)
    if (success) {
      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
      // 削除成功後、リストを再読み込み
      load(false)
    }
  }

  // 削除モーダルを閉じる処理
  const handleDeleteModalClose = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
    }
  }

  // 足跡
  const {
    isLoading: loadingAsiato,
    asiatoList,
    statData,
  } = useOwnAsiato(start, end, 'all')

  const statBaseCards = [
    {
      number: 1,
      title: 'プロフィールを完成させる',
      content:
        'マッチング検索で見つかりやすいようにプロフィールを完成させましょう。',
      buttonText: 'プロフィール編集',
      actionCode: 'updateProfile',
      onClick: () =>
        navigate(`/enterprises/detail/${user?.kuchikomiEnterpriseId}`),
    },
    {
      number: 2,
      title: 'AIに相談する',
      content:
        'AIに事業アイディアの相談や、現状の課題点について相談してみよう。',
      buttonText: 'AIに相談する',
      actionCode: 'askAi',
      onClick: () => navigate('/myidea'),
    },
    {
      number: 3,
      title: '共創パートナーを検索する',
      content:
        'レコメンド企業、マッチング、検索から共創パートナーを探して、マッチングリクエストを送信してみよう。',
      buttonText: 'パートナー検索',
      actionCode: 'searchPartners',
      onClick: () => navigate('/recruitment'),
    },
    {
      number: 4,
      title: 'メッセージを送信する',
      content: 'マッチングした企業へメッセージを送ってみよう。',
      buttonText: 'メッセージ送信',
      actionCode: 'sendMessage',
      onClick: () => navigate('/chat'),
    },
  ]

  return (
    <AuthenticatedLayout>
      <>
        {/* スタートアップガイド */}
        <h2 className="text-2xl font-bold mb-4">スタートアップガイド</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {statBaseCards.map((card, index) => (
            <BaseCard key={index} className="h-full">
              <>
                {initializedGuideStatus ? (
                  <div className="flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex-shrink-0 flex justify-center leading-none items-center rounded-full bg-main-green text-main-white font-bold text-xl">
                        {card.number}
                      </div>
                      <p className="font-bold">{card.title}</p>
                    </div>
                    <div className="mb-4 flex-grow">{card.content}</div>
                    <FlatButton
                      onClick={card.onClick}
                      className="w-full mt-auto"
                      outline={
                        completedActions.includes(card.actionCode)
                          ? false
                          : true
                      }
                    >
                      {completedActions.includes(card.actionCode)
                        ? '完了！'
                        : card.buttonText}
                    </FlatButton>
                  </div>
                ) : (
                  <LoadingSpinner className="my-20" />
                )}
              </>
            </BaseCard>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* クチコミ評価 */}
          <div>
            <h2 className="text-2xl font-bold mb-4">クチコミ評価</h2>
            <BaseCard>
              {initializedEnterprise && enterprise ? (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <EvaluationStarIcon
                      evaluation={enterprise.evaluationAverage ?? 0}
                      size="lg"
                    />
                    <p className="text-3xl font-bold relative top-[2px]">
                      {(enterprise.evaluationAverage ?? 0).toFixed(1)}
                    </p>
                    <span className="px-2"></span>
                    <p>
                      クチコミ件数：
                      {enterprise.kuchikomiCount?.toLocaleString() ?? '-'}件
                    </p>
                  </div>
                  <div className="flex items-center gap-6 mb-4">
                    <div className="flex items-center gap-1">
                      <GivedPointIcon size="sm" />
                      <span className="text-main-green text-sm relative top-[1px]">
                        ポイント贈呈数：
                        {enterprise.givedPointCount?.toLocaleString() ?? '-'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <HoldPointIcon size="sm" />
                      <span className="text-main-green text-sm relative top-[1px]">
                        ポイント保有数：
                        {enterprise.holdPointCount?.toLocaleString() ?? '-'}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm mb-6">{enterprise?.description}</p>
                  <div className="grid grid-cols-2 gap-4">
                    {enterprise.enterpriseCode && enterprise.enterpriseRank && (
                      <div className="pb-2">
                        <RankItem
                          label="団体別クチコミランキング"
                          title={enterprise.enterpriseCode.value}
                          rank={enterprise.enterpriseRank}
                        />
                      </div>
                    )}
                    {enterprise.industryParentCode &&
                      enterprise.industryRank && (
                        <div className="pb-2">
                          <RankItem
                            label="業界別クチコミランキング"
                            title={enterprise.industryParentCode.value}
                            rank={enterprise.industryRank}
                          />
                        </div>
                      )}
                    {enterprise.prefecture && enterprise.prefectureRank && (
                      <div className="pb-2">
                        <RankItem
                          label="都道府県別クチコミランキング"
                          title={enterprise.prefecture}
                          rank={enterprise.prefectureRank}
                        />
                      </div>
                    )}
                    {enterprise.sdgsRank && enterprise.sdgsRank.length > 0 && (
                      <div className="pb-2">
                        <SDGsRankItem sdgsRank={enterprise.sdgsRank} />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <LoadingSpinner className="my-32" />
              )}
            </BaseCard>
          </div>
          {/* 新着クチコミ */}
          <div>
            <h2 className="text-2xl font-bold mb-4">
              新着クチコミ（{kuchikomis.length}件）
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {kuchikomis.slice(0, 2).map((kuchikomi, index) => (
                <BaseCard key={index}>
                  <>
                    {initializedEnterprise && enterprise && initialized ? (
                      <>
                        <div className="mb-4">
                          <div className="flex justify-between">
                            <div className="flex items-center gap-2">
                              <img
                                src={kuchikomi.reviewer.iconUrl}
                                alt="ユーザーアイコン"
                                className="w-6 h-6 rounded-full object-contain"
                              />
                              <span className="font-bold">
                                {kuchikomi.reviewer.displayName}
                              </span>
                            </div>
                            <div>
                              <EvaluationStarIcon evaluation={5} size="md" />
                            </div>
                          </div>
                          <div>
                            <p>{kuchikomi.kuchikomi}</p>
                          </div>
                          <div className="flex justify-between">
                            <div className="flex items-center gap-1">
                              <IinePointIcon size="sm" />
                              <span className="text-main-green text-sm relative top-[1px]">
                                4
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <img
                                src={getPublicPath(
                                  `assets/share/img/sdgs/${kuchikomi.category?.code}.png`
                                )}
                                alt={kuchikomi.category?.value}
                                className="w-6 h-6 object-contain"
                              />
                              <p>{kuchikomi.category?.value}</p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <LoadingSpinner className="my-20" />
                    )}
                  </>
                </BaseCard>
              ))}
            </div>
            {kuchikomis.length === 0 && (
              <BaseCard className="mb-12">
                <div className="text-center py-8">
                  <p className="text-main-darkgray">まだクチコミがありません</p>
                </div>
              </BaseCard>
            )}
          </div>
        </div>

        {/* 事業アイディア */}
        <h2 className="text-2xl font-bold mb-4">事業アイディア</h2>
        {isLoading ? (
          <LoadingSpinner className="my-20" />
        ) : recommendations.length === 0 ? (
          <BaseCard className="mb-12">
            <div className="text-center py-8">
              <p className="text-main-darkgray">
                まだ事業アイディアがありません
              </p>
            </div>
          </BaseCard>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            {recommendations.slice(0, 3).map((recommendation, index) => (
              <BusinessIdeaCard
                key={index}
                recommendation={recommendation}
                initialized={!isLoading}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* 共創パートナー募集 */}
        <h2 className="text-2xl font-bold mb-4">共創パートナー募集</h2>
        {loadingRecruitment ? (
          <LoadingSpinner className="my-20" />
        ) : recruitments.length === 0 ? (
          <BaseCard className="mb-12">
            <div className="text-center py-8">
              <p className="text-main-darkgray">
                まだ共創パートナー募集がありません
              </p>
            </div>
          </BaseCard>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {recruitments.slice(0, 4).map((rec, index) => (
              <PartnerRecruitmentCard
                key={index}
                recruitment={rec}
                initialized={!loadingRecruitment}
                onClick={() => navigate(`/recruitment/${rec.id}`)}
              />
            ))}
          </div>
        )}

        {/* お気に入り企業 */}
        <h2 className="text-2xl font-bold mb-4">お気に入り企業</h2>
        {loadingFavorite ? (
          <LoadingSpinner className="my-20" />
        ) : enterprises.length === 0 ? (
          <BaseCard className="mb-12">
            <div className="text-center py-8">
              <p className="text-main-darkgray">
                まだお気に入り企業がありません
              </p>
            </div>
          </BaseCard>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            {enterprises.slice(0, 3).map((ent, index) => (
              <EnterpriseCard
                key={index}
                enterprise={ent}
                initialized={!loadingFavorite}
              />
            ))}
          </div>
        )}

        {/* 足跡 */}
        <h2 className="text-2xl font-bold mb-4">足跡</h2>

        {loadingAsiato ? (
          <LoadingSpinner className="my-20" />
        ) : asiatoList.length === 0 ? (
          <BaseCard className="mb-12">
            <div className="text-center py-8">
              <p className="text-main-darkgray">まだ訪問企業がありません</p>
            </div>
          </BaseCard>
        ) : (
          <>
            {/* 足跡統計 */}
            {asiatoList && asiatoList.length > 0 && (
              <StatCard
                displayCount={
                  statData?.displayCount ||
                  asiatoList.reduce(
                    (total, asiato) => total + asiato.displayCount,
                    0
                  )
                }
                displayEnterpriseCount={
                  statData?.displayEnterpriseCount || asiatoList.length
                }
                matchingRate={
                  statData?.matchingRate ||
                  (() => {
                    const validRates = asiatoList.map(
                      (asiato) => asiato.enterprise.matchingRate
                    )

                    return validRates.length > 0
                      ? Math.round(
                          validRates.reduce((sum, rate) => sum + rate, 0) /
                            validRates.length
                        )
                      : 0
                  })()
                }
              />
            )}

            <div className="flex flex-col gap-4 mb-12">
              {asiatoList.slice(0, 3).map((a, index) => (
                <EnterpriseStatCard
                  key={index}
                  asiato={a}
                  initialized={!loadingAsiato}
                />
              ))}
            </div>
          </>
        )}

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteModalClose}
          onConfirm={handleDeleteConfirm}
          itemName={deleteTarget?.name || '事業アイデア'}
          isDeleting={isDeleting}
        />
      </>
    </AuthenticatedLayout>
  )
}

export default TopPage
